/**
 * Complementary products popup + pre-flight plan resolver.
 *
 * On every product-page Add-to-Cart submit:
 *   1. Pre-flight GET /cart.js to resolve the "winning" selling_plan.
 *      Rule mirrors cart-plan-unifier.js::evaluateAndCascade:
 *        - form's selling_plan wins if set
 *        - else: first cart line with a plan wins
 *        - else: one-time
 *   2. POST the base item straight away with the winner applied. The add never
 *      waits on the popup: shoppers who left or went Back while it was open
 *      lost the item.
 *   3. If the popup is enabled and recommendations exist for the zone, show
 *      it as "added, add these too?". Confirm POSTs only the ticked extras,
 *      pre-planned with the winner; No thanks / close just closes. The drawer
 *      opens once the popup closes (straight after the add when there's no
 *      popup).
 *
 * Because every item lands in the final plan state, cart-plan-unifier has
 * nothing to cascade — eliminating the N-per-line /cart/change burst that
 * otherwise fires after each add.
 *
 * Files: sections/complementary-popup.liquid (modal + recs template), layout/theme.liquid (mounts shell).
 */
(function () {
  'use strict';

  var POPUP_ID = 'ComplementaryPopup';
  var SELECTION_KEY = 'yf_delivery_selection';
  var popupEl = null;
  var pendingState = null;
  var busy = false;

  function log() {
    try { console.log.apply(console, ['[cpopup]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  function popup() {
    if (!popupEl || !popupEl.isConnected) popupEl = document.getElementById(POPUP_ID);
    return popupEl;
  }

  function popupEnabled() {
    var p = popup();
    return !!p && p.dataset.enabled === 'true';
  }

  function popupLimit() {
    var p = popup();
    return Math.max(1, parseInt(p && p.dataset.limit, 10) || 5);
  }

  function routes() {
    var r = (window.theme && theme.routes) || {};
    return {
      add: r.cart_add_url || '/cart/add.js',
      get: r.cart_url || '/cart.js'
    };
  }

  function currentZoneHandle() {
    try {
      var raw = localStorage.getItem(SELECTION_KEY);
      if (!raw) return null;
      var sel = JSON.parse(raw);
      if (!sel || !sel.zoneHandle) return null;
      return String(sel.zoneHandle).toLowerCase().trim();
    } catch (e) {
      return null;
    }
  }

  // ---- Popup open/close ---------------------------------------------------

  // Concept 6 modals (quick view, product modal) mark everything outside
  // themselves `inert` while open (setBackgroundInert in theme.js). The popup
  // lives in its own section outside the modal, so when it opens on top of
  // one it would be visible but unclickable - clicks fall through to the
  // modal's media zoom. Lift inert from the popup and its ancestors while it
  // is shown, and hand it back on close if the modal still wants it. A modal
  // that finishes opening after the popup (afterShow runs at the end of its
  // transition) re-applies inert, so keep watching until the popup closes.
  var liftedInert = [];
  var inertObserver = null;

  function liftOne(el) {
    if (!el.inert) return;
    el.inert = false;
    if (liftedInert.indexOf(el) === -1) liftedInert.push(el);
  }

  function liftInert(p) {
    liftedInert = [];
    var chain = [];
    for (var el = p; el && el !== document.body; el = el.parentElement) chain.push(el);
    chain.forEach(liftOne);
    if (inertObserver) inertObserver.disconnect();
    inertObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) { liftOne(m.target); });
    });
    chain.forEach(function (el) {
      inertObserver.observe(el, { attributes: true, attributeFilter: ['inert'] });
    });
  }

  function restoreInert() {
    if (inertObserver) {
      inertObserver.disconnect();
      inertObserver = null;
    }
    var openModals = Array.prototype.filter.call(document.querySelectorAll('[open]'), function (m) {
      return Array.isArray(m.inertTargets);
    });
    liftedInert.forEach(function (el) {
      var stillWanted = openModals.some(function (m) { return m.inertTargets.indexOf(el) !== -1; });
      if (stillWanted) el.inert = true;
    });
    liftedInert = [];
  }

  function openPopup() {
    var p = popup();
    if (!p) return;
    liftInert(p);
    p.hidden = false;
    p.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
  }

  function closePopup() {
    var p = popup();
    if (!p) return;
    // Blur any focused descendant before flipping aria-hidden so the browser
    // doesn't warn about hiding a focused element.
    if (p.contains(document.activeElement) && document.activeElement.blur) {
      try { document.activeElement.blur(); } catch (e) {}
    }
    p.hidden = true;
    p.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    restoreInert();
    var body = p.querySelector('[data-cpopup-recs]');
    if (body) body.innerHTML = '';
    setConfirmBusy(false);
    updateConfirmCount();
    pendingState = null;
    busy = false;
  }

  function setConfirmBusy(b) {
    var p = popup();
    var btn = p && p.querySelector('[data-cpopup-confirm]');
    if (!btn) return;
    if (b) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
  }

  function updateConfirmCount() {
    var p = popup();
    if (!p) return;
    var count = p.querySelector('[data-cpopup-confirm-count]');
    if (!count) return;
    var checked = p.querySelectorAll('[data-cpopup-check]:checked').length;
    if (checked > 0) {
      count.hidden = false;
      count.textContent = ' (+' + checked + ')';
    } else {
      count.hidden = true;
      count.textContent = '';
    }
  }

  // For each card, swap in the subscription-plan discounted price when a
  // "winning" plan is in play. Relies on Liquid having emitted the base price
  // and all selling_plan_allocations as data attrs on the card (see
  // sections/complementary-popup.liquid). If no plan matches, shows base only.
  function applyPricing(container, planId) {
    var cards = container.querySelectorAll('[data-cpopup-card]');
    cards.forEach(function (card) {
      var priceEl = card.querySelector('[data-cpopup-price]');
      if (!priceEl) return;

      var basePrice = parseInt(card.getAttribute('data-base-price'), 10);
      var baseFmt = card.getAttribute('data-base-price-fmt') || '';
      var allocs = [];
      try { allocs = JSON.parse(card.getAttribute('data-allocations') || '[]'); } catch (e) {}

      var match = null;
      if (planId) {
        for (var i = 0; i < allocs.length; i++) {
          if (String(allocs[i].id) === String(planId)) { match = allocs[i]; break; }
        }
      }

      if (match && match.price < basePrice) {
        var savePct = Math.round((basePrice - match.price) / basePrice * 100);
        priceEl.classList.add('cpopup-card__price--has-sub');
        priceEl.innerHTML =
          '<span class="cpopup-card__price-sub"></span>' +
          '<span class="cpopup-card__price-base"></span>' +
          '<span class="cpopup-card__price-save"></span>';
        priceEl.querySelector('.cpopup-card__price-sub').textContent = match.fmt;
        priceEl.querySelector('.cpopup-card__price-base').textContent = baseFmt;
        priceEl.querySelector('.cpopup-card__price-save').textContent = 'Save ' + savePct + '%';
      } else {
        priceEl.classList.remove('cpopup-card__price--has-sub');
        priceEl.innerHTML = '<span class="cpopup-card__price-base"></span>';
        priceEl.querySelector('.cpopup-card__price-base').textContent = baseFmt;
      }
    });
  }

  // Zone-filter recommendation cards by Appstle selling-plan-group membership.
  // The shopper's appstleGroupGid (from yf_delivery_selection) is matched
  // against each card's data-product-plan-groups (rendered from
  // product.selling_plan_groups). A product with no plan groups is hidden —
  // the popup is for subscription add-ons, so a product with no plans isn't
  // a valid candidate.
  function applyZoneFilter(container) {
    var sel = getSelection();
    var wantedGroupSuffix = sel && sel.appstleGroupGid
      ? String(sel.appstleGroupGid).replace(/.*\//, '').toLowerCase()
      : null;

    var cards = container.querySelectorAll('[data-cpopup-card]');
    var visible = 0;
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];

      // No zone selected → show everything (matches site-wide pre-gate default).
      if (!wantedGroupSuffix) { visible++; continue; }

      var groupsAttr = (card.getAttribute('data-product-plan-groups') || '').toLowerCase();
      var planGroups = groupsAttr.split(',').map(function (s) { return s.trim(); }).filter(Boolean);

      var keep = false;
      for (var g = 0; g < planGroups.length; g++) {
        if (planGroups[g] === wantedGroupSuffix || planGroups[g].indexOf(wantedGroupSuffix) !== -1) {
          keep = true;
          break;
        }
      }

      if (keep) {
        visible++;
      } else {
        card.setAttribute('data-hidden', '');
        card.style.display = 'none';
      }
    }
    return visible;
  }

  function getSelection() {
    try {
      var raw = localStorage.getItem(SELECTION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  // ---- Form state + plan resolution --------------------------------------

  function captureFormState(form) {
    var data = new FormData(form);
    var base = {
      id: data.get('id'),
      quantity: parseInt(data.get('quantity'), 10) || 1
    };
    // One-time-only products: ignore any selling_plan even if Appstle silently
    // injected one into the form. Otherwise resolveWinner() would inherit it
    // and stamp the new line with a subscription on /cart/add. Detect via the
    // hidden line property input (reliable Liquid signal).
    var isOneTimeOnly = formIsOneTimeOnly(form);
    var sellingPlan = isOneTimeOnly ? null : data.get('selling_plan');
    if (sellingPlan) base.selling_plan = sellingPlan;

    var properties = {};
    data.forEach(function (value, key) {
      var m = /^properties\[(.+)\]$/.exec(key);
      if (m) properties[m[1]] = value;
    });
    if (Object.keys(properties).length) base.properties = properties;

    // Bundle inputs live outside the form but reference it via form="<id>".
    // Use getAttribute('id') -- form.id is shadowed by the variant-id input.
    var formIdAttr = (form.getAttribute && form.getAttribute('id')) || '';
    var bundleInputs = document.querySelectorAll('[form="' + formIdAttr + '"] input[name="bundles"]:checked');
    var bundles = [];
    for (var i = 0; i < bundleInputs.length; i++) {
      bundles.push({ id: bundleInputs[i].value, quantity: 1 });
    }

    return {
      form: form,
      base: base,
      bundles: bundles,
      sellingPlan: sellingPlan || null,
      winner: null // resolved in preflight
    };
  }

  function resolveProductId(form) {
    if (form.dataset && form.dataset.productId) return form.dataset.productId;
    // CAREFUL: do NOT use form.id here. The form has a child <input name="id">
    // (the variant ID) which shadows form.id when accessed as a property --
    // form.id returns the input element, not the form's id attribute string.
    // getAttribute('id') bypasses the named-child shadow.
    var formIdAttr = (form.getAttribute && form.getAttribute('id')) || '';
    var m = /(\d+)$/.exec(formIdAttr);
    return m ? m[1] : null;
  }

  // Mirror of cart-plan-unifier.js::planIdOf — returns String plan id or null.
  function planIdOf(item) {
    if (item && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      return String(item.selling_plan_allocation.selling_plan.id);
    }
    return null;
  }

  function fetchCart() {
    return fetch(routes().get, {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) { return r.json(); });
  }

  // Mirrors unifier's winner rule (see cart-plan-unifier.js evaluateAndCascade).
  //
  // Special case: if the form carried the explicit-one-time marker
  // (properties._yf_explicit_onetime=1, set by cart-plan-unifier's submit
  // listener when the shopper picks the One-time radio), don't fall back to
  // the cart's existing subscription plan. Honoring the marker here is what
  // keeps the base item from being silently stamped with a plan before
  // /cart/add ever runs.
  function isExplicitOneTimeState(state) {
    return !!(state && state.base && state.base.properties && state.base.properties._yf_explicit_onetime);
  }

  function resolveWinner(state) {
    if (state.sellingPlan) return Promise.resolve(String(state.sellingPlan));
    if (isExplicitOneTimeState(state)) return Promise.resolve(null);
    return fetchCart().then(function (cart) {
      if (!cart || !Array.isArray(cart.items)) return null;
      for (var i = 0; i < cart.items.length; i++) {
        var p = planIdOf(cart.items[i]);
        if (p) return p;
      }
      return null;
    }).catch(function () { return null; });
  }

  // ---- Item building ------------------------------------------------------

  // Look up the rec card for a given variant id and check whether its
  // selling_plan_allocations include the plan we want to apply. The popup
  // filters recs by data-product-zone-handles (zone *handle*) but the
  // winning plan is a selling_plan *id*. If the form's plan came from a URL
  // param for a different zone than the shopper's stored zone, those two
  // can disagree — applying the wrong plan to a rec produces a 422 from
  // /cart/add. So validate before applying; fall back to one-time and let
  // the cart-plan-unifier cascade onto the right plan after the add.
  function complementaryHasPlan(variantId, planId) {
    var p = popup();
    if (!p) return false;
    var cb = p.querySelector('[data-cpopup-check][data-variant-id="' + String(variantId).replace(/"/g, '\\"') + '"]');
    if (!cb) return false;
    var card = cb.closest('[data-cpopup-card]');
    if (!card) return false;
    // One-time-only products never accept a winning subscription plan, even
    // if their selling_plan_allocations technically include it.
    if (card.getAttribute('data-yf-one-time-only') === 'true') return false;
    try {
      var allocs = JSON.parse(card.getAttribute('data-allocations') || '[]');
      for (var i = 0; i < allocs.length; i++) {
        if (String(allocs[i].id) === String(planId)) return true;
      }
    } catch (e) {}
    return false;
  }

  // True if the given product form is tagged one-time-only — base item must
  // never inherit a winning plan from cart state. Detect via the hidden line
  // property input (reliable Liquid signal) rather than form.dataset, since
  // Shopify's Liquid form-tag kwarg booleans render inconsistently.
  function formIsOneTimeOnly(form) {
    return !!(form && form.querySelector && form.querySelector('input[name="properties[_yf_one_time_only]"]'));
  }

  // extrasOnly: the base (and its bundle add-ons) is already in the cart, so
  // build just the ticked recommendations.
  function buildItems(state, complementaryVariantIds, winnerPlan, extrasOnly) {
    var items = [];

    if (!extrasOnly) {
      // Theme-level "bundle" inputs (extra-checkbox add-ons via input[name=bundles]).
      // These are independent products with their own selling_plan_groups, so we
      // never apply the winning plan to them — the unifier cascades after the add.
      // Most products won't have any bundle inputs; this is a no-op for them.
      for (var i = 0; i < state.bundles.length; i++) {
        items.push(Object.assign({}, state.bundles[i]));
      }

      // Base item — carries whatever plan the shopper actually selected on the
      // PDP form (Appstle widget's hidden selling_plan input). One-time-only
      // products never accept an inherited plan, even if cart already has a
      // subscription — they must always land as one-time so the cascade locks
      // in the cart can keep them that way.
      var base = Object.assign({}, state.base);
      var baseOneTimeOnly = formIsOneTimeOnly(state.form);
      if (winnerPlan && !baseOneTimeOnly) base.selling_plan = winnerPlan;
      else delete base.selling_plan;
      items.push(base);
    }

    // Complementary items — apply the winning plan only when the rec's own
    // allocations include it. See complementaryHasPlan() above for why.
    for (var j = 0; j < complementaryVariantIds.length; j++) {
      var it = { id: complementaryVariantIds[j], quantity: 1 };
      // Stamp the one-time-only lock as a hidden line property so the line
      // self-identifies in cart-plan-unifier (no dependency on the
      // server-rendered locks JSON, which is stale immediately after add).
      var card = complementaryCard(complementaryVariantIds[j]);
      if (card && card.getAttribute('data-yf-one-time-only') === 'true') {
        it.properties = Object.assign({}, it.properties || {}, { _yf_one_time_only: '1' });
      }
      if (winnerPlan && complementaryHasPlan(complementaryVariantIds[j], winnerPlan)) {
        it.selling_plan = winnerPlan;
      }
      items.push(it);
    }
    return items;
  }

  function complementaryCard(variantId) {
    var p = popup();
    if (!p) return null;
    var cb = p.querySelector('[data-cpopup-check][data-variant-id="' + String(variantId).replace(/"/g, '\\"') + '"]');
    return cb ? cb.closest('[data-cpopup-card]') : null;
  }

  // ---- Shared cart submit -------------------------------------------------

  function submitButtonsFor(form) {
    var list = [];
    try {
      form.querySelectorAll('[type="submit"]').forEach(function (b) { list.push(b); });
      // Use getAttribute('id') -- form.id is shadowed by the variant-id input.
      var formIdAttr = (form.getAttribute && form.getAttribute('id')) || '';
      if (formIdAttr) {
        document.querySelectorAll('[type="submit"][form="' + formIdAttr + '"]').forEach(function (b) { list.push(b); });
      }
    } catch (e) {}
    return list;
  }

  function setFormBusy(form, isBusy) {
    submitButtonsFor(form).forEach(function (b) {
      if (isBusy) {
        b.setAttribute('aria-disabled', 'true');
        b.setAttribute('aria-busy', 'true');
      } else {
        b.removeAttribute('aria-disabled');
        b.removeAttribute('aria-busy');
      }
    });
  }

  function publishCartError(sourceVariantId, parsed) {
    try {
      if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
          source: 'complementary-popup',
          productVariantId: sourceVariantId,
          errors: parsed.errors || parsed.description,
          message: parsed.message
        });
      }
    } catch (e) {}
  }

  function publishCartUpdate(sourceVariantId, cart) {
    try {
      if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'complementary-popup',
          productVariantId: sourceVariantId,
          cart: cart
        });
      }
    } catch (e) {}
  }

  function openDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.show === 'function') {
      try { drawer.show(); } catch (e) { log('drawer show failed', e); }
    }
  }

  // POST items to /cart/add, refresh cart state, open the drawer.
  // opts.onAdded runs as soon as Shopify accepts the add, before anything
  // that could still fail, so callers never re-add an item that is already
  // in the cart. opts.openDrawer: false leaves the drawer shut.
  function submitCart(items, sourceVariantId, opts) {
    opts = opts || {};
    // Let other components opt in to server-rendered section HTML.
    var sectionsToBundle = [];
    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
        bubbles: true, detail: { sections: sectionsToBundle }
      }));
    } catch (e) {}

    var body = { items: items };
    if (sectionsToBundle.length) {
      body.sections = sectionsToBundle.join(',');
      body.sections_url = window.location.pathname;
    }

    var r = routes();
    return fetch(r.add, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body),
      credentials: 'same-origin'
    })
      .then(function (resp) {
        // Always read body as text first, then try JSON. Shopify's /cart/add
        // 4xx responses don't always include a top-level `status` field —
        // checking resp.ok is the only reliable error signal.
        return resp.text().then(function (text) {
          var parsed = null;
          try { parsed = JSON.parse(text); } catch (e) {}
          return { ok: resp.ok, httpStatus: resp.status, parsed: parsed, text: text };
        });
      })
      .then(function (data) {
        var parsed = data.parsed || {};
        if (!data.ok || parsed.status || parsed.errors) {
          publishCartError(sourceVariantId, parsed);
          var err = new Error(
            parsed.description ||
            parsed.message ||
            (parsed.errors && JSON.stringify(parsed.errors)) ||
            ('Add failed (HTTP ' + data.httpStatus + ')')
          );
          err.parsed = parsed;
          err.httpStatus = data.httpStatus;
          throw err;
        }
        if (opts.onAdded) opts.onAdded();
        return fetchCart().then(function (cartJson) {
          if (parsed.sections) cartJson.sections = parsed.sections;
          publishCartUpdate(sourceVariantId, cartJson);
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsed } }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
          if (opts.openDrawer !== false) openDrawer();
          return parsed;
        });
      });
  }

  // ---- Recommendations fetch ---------------------------------------------

  function fetchRecommendations(productId, limit) {
    var url = '/recommendations/products?section_id=complementary-popup'
      + '&product_id=' + encodeURIComponent(productId)
      + '&limit=' + encodeURIComponent(limit)
      + '&intent=complementary';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('recs ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var src = doc.querySelector('[data-cpopup-recs]');
        return src ? src.innerHTML : '';
      });
  }

  // ---- Fallback: let ProductForm's native handler run --------------------
  // Used only if our preflight fails catastrophically. Resumes the original
  // add-to-cart behaviour (including unifier cascade) so the user isn't stuck.

  function proceedOriginal(form) {
    form.__cpopupBypass = true;
    try {
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    } finally {
      setTimeout(function () { form.__cpopupBypass = false; }, 0);
    }
  }

  // ---- Main interceptor ---------------------------------------------------

  function shouldIntercept(form) {
    if (!form || form.getAttribute('is') !== 'product-form') return false;
    if (form.__cpopupBypass) return false;
    if (document.body.classList.contains('template-cart')) return false;
    if (window.theme && theme.settings && theme.settings.cartType === 'page') return false;

    // The popup should fire on every "user clicks Add" surface that we can
    // intercept. That means:
    //   - the normal product page (template-product)
    //   - any product form inside an open <quick-view> modal
    //   - quick-add buttons inside any .product-card (featured-collection on
    //     the home page, collection grid, recently-viewed, mega-menu recs)
    // All three use the same ProductForm custom element with is="product-form"
    // plus the same data-product-id attribute, so the rest of the interceptor
    // works unchanged once we add them here.
    var isPDP = document.body.classList.contains('template-product');
    var isInQuickView = form.closest && form.closest('quick-view');
    var isInProductCard = form.closest && form.closest('.product-card');
    if (!isPDP && !isInQuickView && !isInProductCard) return false;
    return true;
  }

  function onSubmitCapture(e) {
    var form = e.target;
    if (!shouldIntercept(form)) return;

    var state = captureFormState(form);
    if (!state.base.id) return;

    var productId = resolveProductId(form);
    if (!productId) return;

    e.preventDefault();
    e.stopPropagation();

    setFormBusy(form, true);
    var offer = popupEnabled() && !!popup();

    resolveWinner(state)
      .then(function (winner) {
        state.winner = winner;
        // Add first. With extras to offer, the drawer waits until the popup
        // closes so only one panel is open at a time.
        return submitCart(buildItems(state, [], winner), state.base.id, {
          openDrawer: !offer,
          onAdded: function () { state.added = true; }
        });
      })
      .then(function () {
        setFormBusy(form, false);
        if (offer) return offerExtras(state, productId);
      })
      .catch(function (err) {
        log('submit failed', err);
        setFormBusy(form, false);
        // In the cart already: never post it again (that double-added).
        if (state.added) { closePopup(); openDrawer(); return; }
        closePopup();
        // The add itself failed. Last resort: let the native form handler
        // try, which also shows Shopify's error message on the form.
        proceedOriginal(form);
      });
  }

  // The base is already in the cart: offer the zone's extras, or just open
  // the drawer when there are none.
  function offerExtras(state, productId) {
    return fetchRecommendations(productId, popupLimit())
      .then(function (html) {
        var p = popup();
        var recsContainer = p && p.querySelector('[data-cpopup-recs]');
        if (!recsContainer || !html) { openDrawer(); return; }
        recsContainer.innerHTML = html;
        if (applyZoneFilter(recsContainer) === 0) { recsContainer.innerHTML = ''; openDrawer(); return; }
        applyPricing(recsContainer, state.winner);
        pendingState = state;
        updateConfirmCount();
        openPopup();
      })
      .catch(function (err) {
        log('recommendations failed', err);
        openDrawer();
      });
  }

  // Confirm = add the ticked extras (the base is already in).
  function handleConfirm() {
    if (busy || !pendingState) return;
    var state = pendingState;
    var checks = popup().querySelectorAll('[data-cpopup-check]:checked');
    var compIds = [];
    for (var i = 0; i < checks.length; i++) compIds.push(checks[i].dataset.variantId);
    if (!compIds.length) { handleSkip(); return; }

    busy = true;
    setConfirmBusy(true);
    submitCart(buildItems(state, compIds, state.winner, true), state.base.id)
      .then(function () {
        closePopup();
      })
      .catch(function (err) {
        log('confirm failed', err);
        var msg = (err && err.message) ? err.message : 'something went wrong adding your items';
        window.alert('Sorry, ' + msg);
        busy = false;
        setConfirmBusy(false);
      });
  }

  // Skip = "No thanks" / close / Escape: the base is already in the cart, so
  // just close and show the drawer.
  function handleSkip() {
    if (busy) return;
    var wasOffering = !!pendingState;
    closePopup();
    if (wasOffering) openDrawer();
  }

  // ---- Event wiring -------------------------------------------------------

  document.addEventListener('submit', onSubmitCapture, true);

  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.matches || !e.target.matches('[data-cpopup-check]')) return;
    updateConfirmCount();
  });

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('[data-cpopup-close]') || e.target.closest('[data-cpopup-skip]')) {
      e.preventDefault();
      handleSkip();
      return;
    }
    if (e.target.closest('[data-cpopup-confirm]')) {
      e.preventDefault();
      handleConfirm();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var p = popup();
    if (!p || p.hidden) return;
    handleSkip();
  });
})();
